import { Download, Hammer, Pencil, Plus, Power, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { PERMISSION } from '@obraplan/core/domain/access/permissions';
import { normalizeName } from '@obraplan/core/domain/shared/naming';
import { MEASURE_BY_LABEL } from '@obraplan/core/domain/takeoff/geometry';
import {
  APPLIES_TO_LABEL,
  countedSideMargin,
  measureLabel,
  serviceElement,
} from '@obraplan/core/domain/takeoff/applies-to';
import type { Service } from '@obraplan/core/domain/takeoff/service';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableWrapper,
} from '@/components/ui/table';
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/states';
import { ConfirmDialog } from '@/components/ui/dialog';
import { TabList, TabPanel } from '@/components/ui/tabs';
import { TaxasMedias } from '@/features/takeoff/TaxasMedias';
import {
  ChecklistFormDialog,
  type ChecklistFormSubmit,
} from '@/features/quality/ChecklistFormDialog';
import { checklistsByService } from '@/features/quality/checklists-by-service';
import { downloadXlsx, toXlsx } from '@/features/reports/xlsx';
import { ServiceFormDialog, type ServiceFormSubmit } from '@/features/takeoff/ServiceFormDialog';
import { catalogoEmPlanilha, nomeDoArquivoDoCatalogo } from '@/features/takeoff/exportar-catalogo';
import { primeiraMaiuscula } from '@/features/takeoff/formato';
import {
  useContainer,
  usePermissionGlobally,
  usePermissionsAnywhere,
  useSessionState,
  useVisibleTabs,
} from '@/features/session/use-session';
import { useCommand } from '@/lib/use-command';
import { useLive } from '@/lib/use-live';

/**
 * Configurações → Serviços: o cadastro global do serviço (Fase 21, fatia 1).
 *
 * É o que o canteiro chama de **atividade**: `ASSENTAMENTO DE ALVENARIA`, e não "Alvenaria
 * — 3º Pavimento", que é a frente onde ele acontece. Até esta fase o serviço existia em três
 * lugares e nenhum era cadastro — a planilha da construtora em código, o acréscimo dos
 * serviços controlados que as FVS pedem, e o que cada obra montava em Quantitativo →
 * Serviços. Aqui eles são um só, e cada obra escolhe dele (ADR-134).
 *
 * Por isso a tela mora em Configurações e não dentro de uma obra: editar aqui vale para
 * todas, e é essa a intenção — atualizar o método construtivo é atualizar o cadastro, e não
 * a tela de cada obra, uma por uma.
 *
 * A busca não é enfeite. São mais de cem serviços hoje e serão mais quando o catálogo
 * crescer; rolar a lista até achar `INFRAESTRURURA ELETRICA` não é caminho (ADR-139).
 */
/**
 * As duas abas: o catálogo, e a taxa de conversão média que as obras orçadas deram para os
 * itens dele (Fase 39). As duas são "do serviço, para todas as obras" — por isso moram juntas.
 */
type Aba = 'catalogo' | 'taxas';

const ABAS: readonly { readonly id: Aba; readonly label: string }[] = [
  { id: 'catalogo', label: 'Catálogo' },
  { id: 'taxas', label: 'Taxas médias' },
];

export function ServicesPage() {
  const container = useContainer();
  const { session } = useSessionState();
  const canManage = usePermissionGlobally(PERMISSION.TAKEOFF_MANAGE);
  const canOpenChecklists = usePermissionsAnywhere().has(PERMISSION.PAGE_CHECKLISTS);

  // Só as abas marcadas no papel (RN-626); a escolhida que ele não vê cai na primeira (RN-629).
  const abasDoPapel = useVisibleTabs('/configuracoes/servicos');
  const abas = ABAS.filter((item) => abasDoPapel.some((tab) => tab.id === item.id));
  const [abaEscolhida, setAba] = useState<Aba>('catalogo');
  const aba = abas.some((item) => item.id === abaEscolhida)
    ? abaEscolhida
    : (abas[0]?.id ?? abaEscolhida);
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Service | undefined>(undefined);
  /**
   * O serviço recém-cadastrado, enquanto a ficha dele é oferecida (RN-235).
   *
   * `oferta` é o convite; `fichaPara` é o formulário já aberto. Dois estados porque são dois
   * passos, e o primeiro existe justamente para o segundo **não** cair na tela de quem só
   * queria cadastrar um serviço: oferecer não é exigir.
   */
  const [oferta, setOferta] = useState<Service | undefined>(undefined);
  const [fichaPara, setFichaPara] = useState<Service | undefined>(undefined);

  const catalog = useLive(
    () =>
      session === undefined
        ? Promise.resolve(undefined)
        : container.listGlobalServices.execute(session),
    [session?.userId],
  );

  // As fichas, só para dizer ao serviço a qual delas ele pertence (RN-297). Pede-se
  // `structure.view`, que esta tela não exige: sem ela a resposta é recusa, e a seção some do
  // formulário em vez de a tela inteira cair.
  const checklistCatalog = useLive(
    () =>
      session === undefined
        ? Promise.resolve(undefined)
        : container.listServiceChecklists.execute({}, session),
    [session?.userId],
  );
  const fichasDoServico = useMemo(
    () =>
      checklistCatalog === undefined || !checklistCatalog.ok
        ? undefined
        : checklistsByService(checklistCatalog.value.checklists),
    [checklistCatalog],
  );

  const entries = useMemo(
    () => (catalog !== undefined && catalog.ok ? catalog.value.entries : []),
    [catalog],
  );
  const disciplines = useMemo(
    () => (catalog !== undefined && catalog.ok ? catalog.value.disciplines : []),
    [catalog],
  );
  const units = useMemo(
    () => (catalog !== undefined && catalog.ok ? catalog.value.units : []),
    [catalog],
  );
  const unitById = useMemo(() => new Map(units.map((unit) => [unit.id, unit])), [units]);

  // A busca varre nome, disciplina e código: quem procura "alvenaria" quer os serviços da
  // disciplina, e quem procura "assentamento" quer o serviço — os dois pela mesma caixa.
  const filtered = useMemo(() => {
    const term = normalizeName(search);
    if (term === '') return entries;
    return entries.filter(
      (entry) =>
        normalizeName(entry.service.name).includes(term) ||
        normalizeName(entry.disciplineName).includes(term) ||
        normalizeName(entry.service.code ?? '').includes(term),
    );
  }, [entries, search]);

  const closeForm = (): void => {
    setFormOpen(false);
    setEditing(undefined);
  };

  const [createService, createState] = useCommand(
    (input: ServiceFormSubmit) => container.createGlobalService.execute(input, session!),
    {
      successMessage: 'Serviço criado.',
      silent: true,
      onSuccess: (service) => {
        closeForm();
        setOferta(service);
      },
    },
  );

  // A ficha do serviço novo, quando alguém aceita a oferta. Serviço sem ficha continua
  // podendo existir: ele se declara na medição, como sempre (RN-219, RN-235).
  const [saveChecklist, checklistState] = useCommand(
    (input: ChecklistFormSubmit) => container.saveServiceChecklist.execute(input, session!),
    {
      successMessage: 'Ficha criada.',
      silent: true,
      onSuccess: () => setFichaPara(undefined),
    },
  );

  const [updateService, updateState] = useCommand(
    (input: ServiceFormSubmit & { serviceId: string }) =>
      container.updateGlobalService.execute(input, session!),
    { successMessage: 'Serviço atualizado.', silent: true, onSuccess: closeForm },
  );

  const [toggleActive] = useCommand(
    (input: { serviceId: string; isActive: boolean }) =>
      container.setGlobalServiceActive.execute(input, session!),
    {
      successMessage: (service) =>
        service.isActive ? 'Serviço reativado.' : 'Serviço desativado.',
    },
  );

  // Os opcionais viajam como `undefined`, e o contrato os lê como ausentes: campo limpo na
  // tela é campo limpo no cadastro, e não "fica o de antes" — espessura em branco não é zero.
  const submitForm = (input: ServiceFormSubmit): void => {
    if (editing === undefined) void createService(input);
    else void updateService({ ...input, serviceId: editing.id });
  };

  /**
   * O cadastro como o formulário da ficha o vê, com o serviço recém-criado dentro.
   *
   * A leitura da tela pode ainda não ter voltado do servidor quando a oferta é aceita, e uma
   * ficha que aponta para um serviço invisível é exatamente o vínculo cego que esta fase
   * acabou de encerrar.
   */
  const servicosDoCatalogo = entries.map((entry) => entry.service);
  const servicosParaFicha =
    fichaPara !== undefined && !servicosDoCatalogo.some((service) => service.id === fichaPara.id)
      ? [...servicosDoCatalogo, fichaPara]
      : servicosDoCatalogo;

  if (catalog === undefined) return <LoadingState label="Carregando os serviços…" />;
  if (!catalog.ok) return <ErrorState message={catalog.error.message} code={catalog.error.code} />;

  const activeCount = entries.filter((entry) => entry.service.isActive).length;
  const semUnidade = entries.filter(
    (entry) => entry.service.isActive && entry.service.unitOfMeasureId === undefined,
  ).length;

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Serviços</h1>
          <p className="text-(--color-content-muted)">
            Catálogo único, igual para todas as obras. É o que a obra chama de atividade —
            &ldquo;alvenaria não estrutural&rdquo; —, e cada obra escolhe daqui os que executa.
          </p>
        </div>
        {aba === 'catalogo' && (
          <div className="flex flex-wrap gap-2">
            {/* Sai o que está na tela: com a busca preenchida, só o que ela achou. Quem
              exporta "alvenaria" quer a lista da alvenaria, e não as cento e cinquenta. */}
            <Button
              variant="secondary"
              disabled={!filtered.some((entry) => entry.service.isActive)}
              onClick={() =>
                downloadXlsx(
                  nomeDoArquivoDoCatalogo(new Date()),
                  toXlsx(catalogoEmPlanilha(filtered, units)),
                )
              }
            >
              <Download aria-hidden="true" />
              Exportar Excel
            </Button>
            {canManage && (
              <Button
                variant="primary"
                onClick={() => {
                  setEditing(undefined);
                  setFormOpen(true);
                }}
              >
                <Plus aria-hidden="true" />
                Novo serviço
              </Button>
            )}
          </div>
        )}
      </header>

      <TabList
        base="servicos"
        items={abas}
        atual={aba}
        onEscolher={setAba}
        label="Seções dos serviços"
        variant="underline"
      />

      {aba === 'taxas' ? (
        <TabPanel base="servicos" id="taxas">
          <TaxasMedias />
        </TabPanel>
      ) : (
        <TabPanel base="servicos" id="catalogo">
          <Card>
            <CardHeader className="flex-col items-stretch gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <CardTitle>
                  {activeCount === 1 ? '1 serviço ativo' : `${String(activeCount)} serviços ativos`}
                  {entries.length > activeCount &&
                    (entries.length - activeCount === 1
                      ? ' · 1 desativado'
                      : ` · ${String(entries.length - activeCount)} desativados`)}
                </CardTitle>
                <CardDescription>
                  {semUnidade === 0
                    ? 'A disciplina diz de quem é o serviço; a unidade e a medida dizem como ele é medido.'
                    : `${String(semUnidade)} ainda sem unidade — quem for medir neles é recusado até alguém escolher uma.`}
                </CardDescription>
              </div>
              <div className="relative flex w-full items-center md:w-auto">
                <Search
                  className="pointer-events-none absolute left-2.5 size-4 text-(--color-content-muted)"
                  aria-hidden="true"
                />
                <Input
                  aria-label="Buscar serviço"
                  className="w-full pl-8 md:w-56"
                  placeholder="Buscar por serviço, disciplina…"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {filtered.length === 0 ? (
                <EmptyState
                  title={entries.length === 0 ? 'Catálogo vazio' : 'Nenhum serviço encontrado'}
                  description={
                    entries.length === 0
                      ? 'Cadastre o primeiro serviço: a disciplina dona, como ele mede e em que unidade.'
                      : 'Nenhum serviço corresponde à busca.'
                  }
                  icon={<Hammer className="size-8" />}
                  {...(canManage && entries.length === 0
                    ? {
                        action: {
                          label: 'Novo serviço',
                          onClick: () => {
                            setEditing(undefined);
                            setFormOpen(true);
                          },
                        },
                      }
                    : {})}
                />
              ) : (
                <TableWrapper>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Serviço</TableHead>
                        <TableHead>Disciplina</TableHead>
                        <TableHead>Aplica a</TableHead>
                        <TableHead>Como mede</TableHead>
                        <TableHead>Espessura</TableHead>
                        <TableHead>Unidade</TableHead>
                        <TableHead>Situação</TableHead>
                        {canManage && (
                          <TableHead className="text-right">
                            <span className="sr-only">Ações</span>
                          </TableHead>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.map(({ service, disciplineName }) => (
                        <TableRow key={service.id}>
                          <TableCell className="font-medium">{service.name}</TableCell>
                          <TableCell className="text-(--color-content-muted)">
                            {disciplineName}
                          </TableCell>
                          {/* O pendente não mede nada até a escolha (D17): marcado, e não um
                          travessão que se confunde com "não se aplica". */}
                          <TableCell>
                            {serviceElement(service) === undefined ? (
                              <Badge tone="warn">Escolha o elemento</Badge>
                            ) : (
                              APPLIES_TO_LABEL[serviceElement(service)!]
                            )}
                          </TableCell>
                          {/* A explicação entre parênteses é o que ensina parede × face, então ela
                          fica inteira: a tabela rola de lado, e quebrar em cinco linhas no
                          celular esconderia a linha seguinte. */}
                          <TableCell className="whitespace-nowrap">
                            {serviceElement(service) === undefined
                              ? MEASURE_BY_LABEL[service.measureBy]
                              : primeiraMaiuscula(
                                  measureLabel(serviceElement(service)!, service.measureBy),
                                )}
                            {/* A margem da soleira e do peitoril mora junto da medida que a usa (RN-308). */}
                            {countedSideMargin(service) > 0 && (
                              <span className="block text-xs text-(--color-content-muted)">
                                + {countedSideMargin(service).toLocaleString('pt-BR')} m de margem
                                para cada lado
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="tabular">
                            {service.layerThickness === undefined
                              ? '—'
                              : `${service.layerThickness.toLocaleString('pt-BR')} m`}
                          </TableCell>
                          <TableCell>
                            {service.unitOfMeasureId === undefined ? (
                              <span className="text-(--color-content-muted)">Sem unidade</span>
                            ) : (
                              (unitById.get(service.unitOfMeasureId)?.name ?? '—')
                            )}
                          </TableCell>
                          <TableCell>
                            {service.isActive ? (
                              <Badge tone="ok">Ativo</Badge>
                            ) : (
                              <Badge tone="neutral">Desativado</Badge>
                            )}
                          </TableCell>
                          {canManage && (
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  aria-label={`Editar ${service.name}`}
                                  onClick={() => {
                                    setEditing(service);
                                    setFormOpen(true);
                                  }}
                                >
                                  <Pencil aria-hidden="true" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  aria-label={
                                    service.isActive
                                      ? `Desativar ${service.name}`
                                      : `Reativar ${service.name}`
                                  }
                                  onClick={() =>
                                    void toggleActive({
                                      serviceId: service.id,
                                      isActive: !service.isActive,
                                    })
                                  }
                                >
                                  <Power aria-hidden="true" />
                                </Button>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableWrapper>
              )}
            </CardContent>
          </Card>
        </TabPanel>
      )}

      <ConfirmDialog
        open={oferta !== undefined}
        onOpenChange={(open) => {
          if (!open) setOferta(undefined);
        }}
        title="Criar a ficha de verificação?"
        description={
          oferta === undefined
            ? ''
            : `${oferta.name} está no cadastro. A FVS é o que a medição vai perguntar quando ele for executado — e um serviço sem ficha continua podendo ser medido, declarando-se na hora.`
        }
        confirmLabel="Criar ficha"
        cancelLabel="Agora não"
        onConfirm={() => {
          setFichaPara(oferta);
          setOferta(undefined);
        }}
      />

      <ChecklistFormDialog
        open={fichaPara !== undefined}
        onOpenChange={(open) => {
          if (!open) setFichaPara(undefined);
        }}
        checklist={undefined}
        services={servicosParaFicha}
        disciplines={disciplines}
        {...(fichaPara === undefined
          ? {}
          : { suggested: { name: fichaPara.name, serviceIds: [fichaPara.id] } })}
        onSubmit={(input) => void saveChecklist(input)}
        isPending={checklistState.isPending}
        errorMessage={checklistState.error?.message}
      />

      <ServiceFormDialog
        open={formOpen}
        onOpenChange={(open) => {
          if (!open) closeForm();
          else setFormOpen(true);
        }}
        service={editing}
        disciplines={disciplines}
        units={units}
        checklists={
          editing === undefined || fichasDoServico === undefined
            ? undefined
            : (fichasDoServico.get(editing.id) ?? [])
        }
        canOpenChecklists={canOpenChecklists}
        onSubmit={submitForm}
        isPending={createState.isPending || updateState.isPending}
        errorMessage={
          editing === undefined ? createState.error?.message : updateState.error?.message
        }
      />
    </div>
  );
}
